// frontend/src/pages/VouchersPage.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { FiEye, FiCheck, FiX } from 'react-icons/fi';
import Loading from '../components/Loading';
import './VouchersPage.css';

const VouchersPage = () => {
  const [asientos, setAsientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedAsiento, setExpandedAsiento] = useState(null);

  // Estados para filtros
  const [filtroNumero, setFiltroNumero] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');

  useEffect(() => {
    cargarAsientos();
  }, []);

  const cargarAsientos = async () => {
    try {
      setLoading(true);
      const baseURL = api?.defaults?.baseURL || '/api/content';
      const API_BASE_URL = baseURL.replace(/\/content\/?$/i, '/contabilidad');

      const response = await api.get(`${API_BASE_URL}/asientos-contables/`);
      setAsientos(response.data.results || response.data);
    } catch (error) {
      console.error('Error al cargar asientos contables:', error);
      toast.error('Error al cargar los asientos contables');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = async (asientoId) => {
    if (expandedAsiento === asientoId) {
      setExpandedAsiento(null);
      return;
    }

    // Si el asiento no tiene detalles cargados, obtenerlos
    const asiento = asientos.find(a => a.id === asientoId);
    if (!asiento.detalles) {
      try {
        const baseURL = api?.defaults?.baseURL || '/api/content';
        const API_BASE_URL = baseURL.replace(/\/content\/?$/i, '/contabilidad');
        const response = await api.get(`${API_BASE_URL}/asientos-contables/${asientoId}/`);

        // Actualizar el asiento con los detalles
        setAsientos(prevAsientos =>
          prevAsientos.map(a => a.id === asientoId ? response.data : a)
        );
      } catch (error) {
        console.error('Error al cargar detalles del asiento:', error);
        toast.error('Error al cargar los detalles del asiento');
        return;
      }
    }

    setExpandedAsiento(asientoId);
  };

  const handleCambiarEstado = async (asientoId, nuevoEstado) => {
    if (!window.confirm(`¿Está seguro de cambiar el estado a "${nuevoEstado}"?`)) {
      return;
    }

    try {
      const baseURL = api?.defaults?.baseURL || '/api/content';
      const API_BASE_URL = baseURL.replace(/\/content\/?$/i, '/contabilidad');

      await api.post(`${API_BASE_URL}/asientos-contables/${asientoId}/cambiar-estado/`, {
        estado: nuevoEstado
      });

      toast.success(`Estado cambiado a ${nuevoEstado}`);
      cargarAsientos();
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      const errorMsg = error.response?.data?.error || 'Error al cambiar el estado';
      toast.error(errorMsg);
    }
  };

  const getEstadoBadgeClass = (estado) => {
    const estadoMap = {
      'borrador': 'badge-borrador',
      'aprobado': 'badge-aprobado',
      'anulado': 'badge-anulado'
    };
    return estadoMap[estado] || 'badge-default';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL');
  };

  // Aplicar filtros
  const asientosFiltrados = asientos.filter(asiento => {
    const cumpleFiltroNumero = !filtroNumero ||
      asiento.numero_asiento.toLowerCase().includes(filtroNumero.toLowerCase());
    const cumpleFiltroEstado = !filtroEstado || asiento.estado === filtroEstado;

    let cumpleFiltroFecha = true;
    if (filtroFechaDesde || filtroFechaHasta) {
      const fechaAsiento = new Date(asiento.fecha);
      if (filtroFechaDesde) {
        const fechaDesde = new Date(filtroFechaDesde);
        cumpleFiltroFecha = cumpleFiltroFecha && fechaAsiento >= fechaDesde;
      }
      if (filtroFechaHasta) {
        const fechaHasta = new Date(filtroFechaHasta);
        cumpleFiltroFecha = cumpleFiltroFecha && fechaAsiento <= fechaHasta;
      }
    }

    return cumpleFiltroNumero && cumpleFiltroEstado && cumpleFiltroFecha;
  });

  // Calcular estadísticas
  const calcularEstadisticas = () => {
    const stats = {
      total: asientosFiltrados.length,
      borrador: 0,
      aprobado: 0,
      anulado: 0,
      balanceados: 0,
      desbalanceados: 0
    };

    asientosFiltrados.forEach(asiento => {
      stats[asiento.estado]++;
      if (asiento.balanceado) {
        stats.balanceados++;
      } else {
        stats.desbalanceados++;
      }
    });

    return stats;
  };

  const estadisticas = calcularEstadisticas();

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="vouchers-page">
      <div className="page-header">
        <h1>Vouchers (Asientos Contables)</h1>
      </div>

      {/* Estadísticas */}
      <div className="estadisticas-grid">
        <div className="stat-card">
          <div className="stat-label">Total Asientos</div>
          <div className="stat-value">{estadisticas.total}</div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: '#6b7280' }}>
          <div className="stat-label">Borrador</div>
          <div className="stat-value">{estadisticas.borrador}</div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: '#10b981' }}>
          <div className="stat-label">Aprobados</div>
          <div className="stat-value">{estadisticas.aprobado}</div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: '#ef4444' }}>
          <div className="stat-label">Anulados</div>
          <div className="stat-value">{estadisticas.anulado}</div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: '#10b981' }}>
          <div className="stat-label">Balanceados</div>
          <div className="stat-value">{estadisticas.balanceados}</div>
        </div>
        {estadisticas.desbalanceados > 0 && (
          <div className="stat-card" style={{ borderLeftColor: '#ef4444' }}>
            <div className="stat-label">Desbalanceados</div>
            <div className="stat-value">{estadisticas.desbalanceados}</div>
          </div>
        )}
      </div>

      {/* Filtros */}
      <div className="filtros-container">
        <input
          type="text"
          placeholder="Buscar por número..."
          value={filtroNumero}
          onChange={(e) => setFiltroNumero(e.target.value)}
          className="input-filtro"
        />
        <select
          value={filtroEstado}
          onChange={(e) => setFiltroEstado(e.target.value)}
          className="select-filtro"
        >
          <option value="">Todos los estados</option>
          <option value="borrador">Borrador</option>
          <option value="aprobado">Aprobado</option>
          <option value="anulado">Anulado</option>
        </select>
        <input
          type="date"
          placeholder="Fecha desde..."
          value={filtroFechaDesde}
          onChange={(e) => setFiltroFechaDesde(e.target.value)}
          className="input-filtro"
        />
        <input
          type="date"
          placeholder="Fecha hasta..."
          value={filtroFechaHasta}
          onChange={(e) => setFiltroFechaHasta(e.target.value)}
          className="input-filtro"
        />
      </div>

      {/* Tabla de asientos */}
      <div className="asientos-table-container">
        {asientosFiltrados.length === 0 ? (
          <div className="empty-state">
            <p>No se encontraron asientos contables</p>
          </div>
        ) : (
          <table className="asientos-table">
            <thead>
              <tr>
                <th>Número</th>
                <th>Fecha</th>
                <th>Glosa</th>
                <th>Documento</th>
                <th>Debe</th>
                <th>Haber</th>
                <th>Balance</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {asientosFiltrados.map(asiento => (
                <React.Fragment key={asiento.id}>
                  <tr
                    className={expandedAsiento === asiento.id ? 'expanded' : ''}
                    onClick={() => toggleExpand(asiento.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="numero-asiento">
                      {asiento.numero_asiento}
                    </td>
                    <td>{formatDate(asiento.fecha)}</td>
                    <td className="glosa-cell">{asiento.glosa}</td>
                    <td>{asiento.documento_legal_numero || '-'}</td>
                    <td className="monto-debe">
                      {formatCurrency(asiento.monto_debe)}
                    </td>
                    <td className="monto-haber">
                      {formatCurrency(asiento.monto_haber)}
                    </td>
                    <td className="balance-cell">
                      {asiento.balanceado ? (
                        <span className="balance-ok">
                          <FiCheck /> OK
                        </span>
                      ) : (
                        <span className="balance-error">
                          <FiX /> Desbalanceado
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${getEstadoBadgeClass(asiento.estado)}`}>
                        {asiento.estado_display}
                      </span>
                    </td>
                    <td className="acciones-cell" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn-icon"
                        onClick={() => toggleExpand(asiento.id)}
                        title="Ver detalles"
                      >
                        <FiEye />
                      </button>
                      {asiento.estado === 'borrador' && asiento.balanceado && (
                        <button
                          className="btn-icon btn-success"
                          onClick={() => handleCambiarEstado(asiento.id, 'aprobado')}
                          title="Aprobar"
                        >
                          <FiCheck />
                        </button>
                      )}
                      {asiento.estado !== 'anulado' && (
                        <button
                          className="btn-icon btn-danger"
                          onClick={() => handleCambiarEstado(asiento.id, 'anulado')}
                          title="Anular"
                        >
                          <FiX />
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandedAsiento === asiento.id && asiento.detalles && asiento.detalles.length > 0 && (
                    <tr className="detalles-row">
                      <td colSpan="9">
                        <div className="detalles-container">
                          <h4>Detalles del Asiento</h4>
                          <table className="detalles-table">
                            <thead>
                              <tr>
                                <th>Cuenta</th>
                                <th>Glosa</th>
                                <th>Debe</th>
                                <th>Haber</th>
                                <th>Proyecto</th>
                              </tr>
                            </thead>
                            <tbody>
                              {asiento.detalles.map((detalle, index) => (
                                <tr key={index}>
                                  <td>
                                    <span className="cuenta-codigo">{detalle.plan_cuenta_codigo}</span>
                                    {' - '}
                                    <span className="cuenta-nombre">{detalle.plan_cuenta_nombre}</span>
                                  </td>
                                  <td>{detalle.glosa}</td>
                                  <td className="monto-debe">
                                    {detalle.tipo_movimiento === 'debe' ? formatCurrency(detalle.monto) : '-'}
                                  </td>
                                  <td className="monto-haber">
                                    {detalle.tipo_movimiento === 'haber' ? formatCurrency(detalle.monto) : '-'}
                                  </td>
                                  <td>{detalle.proyecto_nombre || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <div className="totales-asiento">
                            <div className="total-row">
                              <span>Total Debe:</span>
                              <span>{formatCurrency(asiento.monto_debe)}</span>
                            </div>
                            <div className="total-row">
                              <span>Total Haber:</span>
                              <span>{formatCurrency(asiento.monto_haber)}</span>
                            </div>
                            <div className={`total-row ${asiento.balanceado ? 'total-balanceado' : 'total-desbalanceado'}`}>
                              <span>Balance:</span>
                              <span>
                                {asiento.balanceado ? (
                                  <><FiCheck /> Balanceado</>
                                ) : (
                                  <><FiX /> Diferencia: {formatCurrency(Math.abs(asiento.monto_debe - asiento.monto_haber))}</>
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default VouchersPage;
